import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Eye, RefreshCw, Shield, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const numberFormat = new Intl.NumberFormat();

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const StatCard = ({ label, value, detail, icon: Icon }) => (
  <Card>
    <CardContent className="flex items-start justify-between gap-4 p-6">
      <div>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="mt-2 text-3xl font-bold tracking-tight text-purple-950">{numberFormat.format(value || 0)}</p>
        <p className="mt-1 text-xs text-gray-500">{detail}</p>
      </div>
      <span className="rounded-xl bg-purple-50 p-3 text-purple-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
    </CardContent>
  </Card>
);

const RankedList = ({ title, items, nameKey }) => {
  const maximum = Math.max(...items.map((item) => item.pageviews), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg text-purple-950">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-5 text-sm text-gray-500">No data for this period.</p>
        ) : (
          <ol className="space-y-4">
            {items.map((item, index) => (
              <li key={`${item[nameKey]}-${index}`}>
                <div className="mb-1 flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0 break-all font-medium text-gray-800">{item[nameKey] || 'Unknown'}</span>
                  <span className="shrink-0 text-gray-600">{numberFormat.format(item.pageviews)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-purple-600"
                    style={{ width: `${Math.max(3, (item.pageviews / maximum) * 100)}%` }}
                    aria-hidden="true"
                  />
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

const AdminAnalyticsPage = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user || user.role !== 'admin') {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError('');
    fetch(`${API_BASE}/api/admin/analytics?days=${days}`, { credentials: 'include' })
      .then(async (response) => {
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.detail || 'Could not load website stats.');
        if (active) setData(result);
      })
      .catch((fetchError) => {
        if (active) setError(fetchError.message || 'Could not load website stats.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authLoading, user, days, refreshCount]);

  const daily = data?.daily || [];
  const maximumPageviews = useMemo(
    () => Math.max(...daily.map((item) => item.pageviews), 1),
    [daily]
  );
  const peakDay = useMemo(
    () => daily.reduce((peak, item) => (item.pageviews > (peak?.pageviews ?? -1) ? item : peak), null),
    [daily]
  );

  if (authLoading) {
    return <main className="min-h-screen bg-gray-50 px-4 pt-28 text-center text-gray-600">Checking administrator access…</main>;
  }

  if (!user || user.role !== 'admin') {
    return (
      <main className="min-h-screen bg-gray-50 px-4 pt-28">
        <Card className="mx-auto max-w-xl">
          <CardHeader><CardTitle>Administrator access required</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-gray-600">
            <p>This page is available only to administrator accounts.</p>
            <Link to="/" className="inline-flex items-center gap-2 text-purple-700 hover:underline">
              <ArrowLeft className="h-4 w-4" /> Return to the site
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 pt-20">
      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-6 sm:px-6 lg:px-8">
          <div>
            <Link to="/admin-dashboard" className="mb-2 inline-flex items-center gap-2 text-sm text-purple-700 hover:underline">
              <ArrowLeft className="h-4 w-4" /> Admin workspace
            </Link>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">Admin workspace</p>
            <h1 className="text-3xl font-bold text-purple-900">Website stats</h1>
            <p className="mt-1 text-gray-600">Traffic from the PostHog project connected to this website.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700">
              <Shield className="h-4 w-4" /> Administrator
            </span>
            <label className="sr-only" htmlFor="analytics-range">Date range</label>
            <select
              id="analytics-range"
              aria-label="Date range"
              className="h-10 rounded-md border border-purple-200 bg-white px-3 text-sm font-medium text-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
              value={days}
              onChange={(event) => setDays(event.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRefreshCount((count) => count + 1)}
              disabled={loading}
              aria-label="Refresh website stats"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={logout} variant="outline">Log out</Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}

        <section className="grid gap-4 sm:grid-cols-2" aria-label="Website traffic totals">
          <StatCard
            label="Pageviews"
            value={data?.totals?.pageviews}
            detail={`Last ${days} days`}
            icon={Eye}
          />
          <StatCard
            label="Unique visitors"
            value={data?.totals?.unique_visitors}
            detail="Distinct PostHog persons"
            icon={Users}
          />
        </section>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-purple-950">
              <BarChart3 className="h-5 w-5 text-purple-700" /> Daily pageviews
            </CardTitle>
            <p className="text-sm text-gray-500">Pageview events by day · last {days} days</p>
          </CardHeader>
          <CardContent>
            {loading && !data ? (
              <p className="py-12 text-center text-sm text-gray-500">Loading website stats…</p>
            ) : daily.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">No pageview data for this period.</p>
            ) : (
              <div
                className="overflow-x-auto"
                role="img"
                aria-label={`Daily pageviews for the last ${days} days`}
              >
                <div className="flex h-56 min-w-[560px] items-end gap-1 border-b border-l border-gray-200 px-2">
                  {daily.map((item, index) => {
                    const percent = item.pageviews === 0
                      ? 0
                      : Math.max(3, (item.pageviews / maximumPageviews) * 100);
                    const labelStep = Math.max(1, Math.ceil(daily.length / 7));
                    const showLabel = index === 0 || index === daily.length - 1 || index % labelStep === 0;
                    return (
                      <div
                        key={item.date}
                        className="flex h-full min-w-0 flex-1 flex-col items-center justify-end"
                        title={`${formatDate(item.date)}: ${numberFormat.format(item.pageviews)} pageviews`}
                      >
                        <div className="flex h-[82%] w-full items-end">
                          <div
                            className="w-full rounded-t-sm bg-purple-600 transition-opacity hover:opacity-75"
                            style={{ height: `${percent}%` }}
                          />
                        </div>
                        <span className="h-8 pt-2 text-[10px] text-gray-500">
                          {showLabel ? formatDate(item.date) : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-right text-xs text-gray-500">
                  {peakDay
                    ? `Peak day: ${formatDate(peakDay.date)} · ${numberFormat.format(peakDay.pageviews)} pageviews`
                    : 'No pageviews in this period'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <section className="grid gap-6 lg:grid-cols-2" aria-label="Traffic breakdowns">
          <RankedList title="Top pages" items={data?.top_pages || []} nameKey="url" />
          <RankedList title="Top referrers" items={data?.referrers || []} nameKey="domain" />
        </section>

        {loading && data && (
          <p className="text-right text-xs text-gray-500">Updating stats…</p>
        )}
      </div>
    </main>
  );
};

export default AdminAnalyticsPage;
